<template>
  <div>
    <SiteHero
      pageTitle="Free Trial"
      pageDescription="Get a Free Trial"
    />
  
    <div class="page-content">
      <div class="max-w-2xl mx-auto px-4 py-12">
        <SubmissionSuccess v-if="isSubmitted" />
        
        <form v-else @submit.prevent="submitForm" class="space-y-8">
          <div>
            <label for="location" class="block text-base font-medium text-gray-700 tracking-wide mb-2">
              Location <span class="text-red-600">*</span>
            </label>
            <select 
              id="location" 
              v-model="formData.location"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black px-3 py-3 text-lg tracking-wide"
              required
              @blur="validateField('location')"
              aria-required="true"
            >
              <option value="" disabled>Select a location</option>
              <option value="Irvine">Irvine</option>
              <option value="Buena Park">Buena Park</option>
            </select>
          </div>

          <div>
            <label for="name" class="block text-base font-medium text-gray-700 tracking-wide mb-2">
              Name <span class="text-red-600">*</span>
            </label>
            <input 
              type="text" 
              id="name" 
              v-model="formData.name"
              placeholder="Full Name"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black px-3 py-3 text-lg tracking-wide"
              required
              @blur="validateField('name')"
              aria-required="true"
            >
          </div>

          <div>
            <label for="email" class="block text-base font-medium text-gray-700 tracking-wide mb-2">
              Email <span class="text-red-600">*</span>
            </label>
            <input 
              type="email" 
              id="email" 
              v-model="formData.email"
              placeholder="email@example.com"
              maxlength="36"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black px-3 py-3 text-lg tracking-wide"
              required
              @blur="validateField('email')"
              aria-required="true"
            >
            <span class="text-sm text-gray-500 mt-1 block text-right">{{ formData.email.length }}/36</span>
          </div>

          <div>
            <label for="phone" class="block text-base font-medium text-gray-700 tracking-wide mb-2">Phone Number (Optional)</label>
            <input 
              type="tel" 
              id="phone" 
              v-model="formData.phone"
              maxlength="14"
              placeholder="7141234567"
              @input="formatPhoneNumber"
              @keypress="validateNumberInput"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black px-3 py-3 text-lg tracking-wide"
              aria-required="false"
            >
            <span class="text-sm text-gray-500 mt-1 block text-right">{{ formData.phone.length }}/14</span>
          </div>

          <div>
            <div class="block text-base font-medium text-gray-700 tracking-wide mb-2">Programs of Interest (Optional)</div>
            <AvailablePrograms v-model="formData.programs" />
          </div>

          <div>
            <label for="message" class="block text-base font-medium text-gray-700 tracking-wide mb-2">Message (Optional)</label>
            <textarea 
              id="message" 
              v-model="formData.message"
              rows="4"
              maxlength="300"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black px-3 py-3 text-lg tracking-wide"
              aria-required="false"
            ></textarea>
            <span class="text-sm text-gray-500 mt-1 block text-right">{{ formData.message.length }}/300</span>
          </div>

          <!-- Required Fields Section -->
          <div v-if="!isFormValid" class="bg-gray-50 p-6 rounded-lg" role="alert">
            <p class="font-medium text-gray-700 mb-3 text-lg tracking-wide">Please complete the following:</p>
            <ul class="list-disc list-inside space-y-2 text-base text-gray-600 tracking-wide">
              <li v-if="!formData.location.trim()">Select a location</li>
              <li v-if="!formData.name.trim()">Enter your name</li>
              <li v-if="!formData.email.trim()">Enter your email address</li>
              <li v-if="formData.email.trim() && !isValidEmail(formData.email)">Enter a valid email address</li>
            </ul>
          </div>

          <!-- Add error message display -->
          <div v-if="errors.submission" class="bg-red-50 p-4 rounded-md" role="alert">
            <p class="text-red-600">{{ errors.submission }}</p>
          </div>

          <div>
            <button 
              type="submit"
              class="w-full bg-black text-white py-3 px-6 rounded-md hover:bg-gray-800 transition-colors duration-200"
              :class="{ 
                'opacity-50 cursor-not-allowed': !isFormValid || isSubmitting,
                'bg-gray-400': isSubmitting 
              }"
              :disabled="!isFormValid || isSubmitting"
            >
              <span v-if="isSubmitting">Sending...</span>
              <span v-else>Submit</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <SiteFooter />
  </div>
</template>
  
<script>
import SiteFooter from '../components/Footer/SiteFooter.vue';
import SiteHero from '../components/Hero/SiteHero.vue';
import AvailablePrograms from '../components/FreeTrial/AvailablePrograms.vue';
import SubmissionSuccess from '../components/FreeTrial/SubmissionSuccess.vue';
import '../assets/styles/page.css';
import emailjs from '@emailjs/browser';
  
export default {
  name: 'FreeTrialPage',
  components: {
    SiteFooter,
    SiteHero,
    AvailablePrograms,
    SubmissionSuccess
  },
  data() {
    return {
      isSubmitted: false,
      isSubmitting: false,
      formData: {
        location: '',
        name: '',
        email: '',
        phone: '',
        programs: {
          jiuJitsu: false,
          wrestling: false,
          kidsPrograms: false,
          muayThai: false,
          strength: false,
          personalTraining: false
        },
        message: ''
      },
      errors: {
        location: '',
        name: '',
        email: '',
        submission: ''
      }
    }
  },
  computed: {
    isFormValid() {
      const hasLocation = this.formData.location && this.formData.location.trim().length > 0;
      const hasName = this.formData.name && this.formData.name.trim().length > 0;
      const hasValidEmail = this.formData.email && this.isValidEmail(this.formData.email);
      
      return hasLocation && hasName && hasValidEmail;
    }
  },
  mounted() {
    if (!process.env.VUE_APP_EMAILJS_PUBLIC_KEY) {
      this.errors.submission = 'Email service configuration is missing';
      return;
    }
    
    try {
      emailjs.init(process.env.VUE_APP_EMAILJS_PUBLIC_KEY);
    } catch (error) {
      this.errors.submission = 'Failed to initialize email service';
    }
  },
  methods: {
    validateField(field) {
      this.errors[field] = ''
      
      switch(field) {
        case 'location':
          if (!this.formData.location.trim()) {
            this.errors.location = 'Location is required'
          }
          break
        case 'name':
          if (!this.formData.name.trim()) {
            this.errors.name = 'Name is required'
          }
          break
        case 'email':
          if (!this.formData.email.trim()) {
            this.errors.email = 'Email is required'
          } else if (!this.isValidEmail(this.formData.email)) {
            this.errors.email = 'Please enter a valid email address'
          }
          break
      }
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    async submitForm(e) {
      e.preventDefault();
      ['location', 'name', 'email'].forEach(field => this.validateField(field));
      
      if (!this.isFormValid) {
        return;
      }

      this.isSubmitting = true;
      this.errors.submission = '';

      try {
        if (!process.env.VUE_APP_EMAILJS_SERVICE_ID || !process.env.VUE_APP_EMAILJS_TEMPLATE_ID) {
          throw new Error('Email service not properly configured');
        }

        const templateParams = {
          location: this.formData.location,
          from_name: this.formData.name,
          from_email: this.formData.email,
          phone_number: this.formData.phone || 'Not provided',
          selected_programs: Object.entries(this.formData.programs)
            .filter(([, isSelected]) => isSelected)
            .map(([program]) => program.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()))
            .join(', ') || 'None selected',
          message: this.formData.message || 'No message provided'
        };

        const response = await emailjs.send(
          process.env.VUE_APP_EMAILJS_SERVICE_ID,
          process.env.VUE_APP_EMAILJS_TEMPLATE_ID,
          templateParams
        );

        if (response.status === 200) {
          this.isSubmitted = true;
          const heroHeight = document.querySelector('.page-content').offsetTop;
          window.scrollTo({ top: heroHeight - 24, behavior: 'smooth' });
        } else {
          throw new Error('Failed to send email');
        }
      } catch (error) {
        /* eslint-disable no-console */
        console.error('Submission error:', error);
        /* eslint-enable no-console */
        this.errors.submission = 'Failed to send message. Please try again later.';
      } finally {
        this.isSubmitting = false;
      }
    },
    validateNumberInput(event) {
      // Only allow numbers
      const charCode = (event.which) ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
        return false;
      }
      return true;
    },
    formatPhoneNumber(event) {
      // Remove all non-digits
      this.formData.phone = event.target.value.replace(/\D/g, '');
    }
  }
};
</script>

<style scoped>
input, textarea {
  @apply border border-gray-300;
}

/* Focus styles for better accessibility */
input:focus, textarea:focus {
  @apply outline-none ring-2 ring-black ring-opacity-50;
}

/* Increase spacing between form sections */
.space-y-8 > * + * {
  margin-top: 2rem;
}
</style>
